import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, FileCheck, CheckCircle2, AlertCircle, Clock, 
  Link2, Upload, FileText, Building2, Scale, Users,
  ChevronDown, ChevronRight, Search, Filter, Download,
  TrendingUp, Eye, Plus, RefreshCw, XCircle, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { apiFetch } from '../services/api_service';


const API_URL = process.env.REACT_APP_BACKEND_URL;

const FRAMEWORKS = [
  { id: 'csrd_esrs', name: 'CSRD/ESRS', icon: '🇪🇺' },
  { id: 'eu_taxonomy', name: 'EU Taxonomy', icon: '🌱' },
  { id: 'csddd', name: 'CSDDD', icon: '⚖️' },
  { id: 'sec_climate', name: 'SEC Climate', icon: '🇺🇸' },
  { id: 'tcfd', name: 'TCFD', icon: '🌡️' },
  { id: 'gri', name: 'GRI', icon: '📊' },
];

const EVIDENCE_TYPES = [
  { id: 'source_document', name: 'Source Document', icon: FileText },
  { id: 'calculation_workpaper', name: 'Calculation Workpaper', icon: FileCheck },
  { id: 'third_party_verification', name: 'Third-Party Verification', icon: Award },
  { id: 'board_resolution', name: 'Board Resolution', icon: Building2 },
  { id: 'management_attestation', name: 'Management Attestation', icon: Users },
  { id: 'audit_report', name: 'Audit Report', icon: Shield },
  { id: 'policy_document', name: 'Policy Document', icon: FileText },
  { id: 'certificate', name: 'Certificate', icon: Award },
];

const TARGET_TYPES = [
  { id: 'esrs_data_point', name: 'ESRS Data Point' },
  { id: 'esrs_disclosure', name: 'ESRS Disclosure' },
  { id: 'csddd_risk', name: 'CSDDD Risk' },
  { id: 'csddd_remediation', name: 'CSDDD Remediation' },
  { id: 'eu_taxonomy_activity', name: 'EU Taxonomy Activity' },
  { id: 'sec_climate_disclosure', name: 'SEC Climate Disclosure' },
  { id: 'xbrl_element', name: 'XBRL Element' },
  { id: 'target', name: 'Target' },
  { id: 'emission_data', name: 'Emission Data' },
];

// Score color helper
const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-600 bg-green-100';
  if (score >= 60) return 'text-yellow-600 bg-yellow-100';
  if (score >= 40) return 'text-orange-600 bg-orange-100';
  return 'text-red-600 bg-red-100';
};

const getScoreBgColor = (score) => {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-yellow-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
};

// Dashboard Overview Component
const DashboardOverview = ({ dashboard, onRefresh }) => {
  if (!dashboard) return null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Evidence</p>
                <p className="text-3xl font-bold">{dashboard.summary?.total_evidence || 0}</p>
              </div>
              <FileText className="h-10 w-10 text-blue-500 opacity-50" />
            </div>
            <div className="mt-2 text-sm">
              <span className="text-green-600">{dashboard.summary?.verified_evidence || 0} verified</span>
              <span className="text-gray-400 mx-2">|</span>
              <span className="text-yellow-600">{dashboard.summary?.pending_evidence || 0} pending</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Evidence Links</p>
                <p className="text-3xl font-bold">{dashboard.summary?.total_links || 0}</p>
              </div>
              <Link2 className="h-10 w-10 text-purple-500 opacity-50" />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Connected to compliance items
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Control Attestations</p>
                <p className="text-3xl font-bold">{dashboard.summary?.total_attestations || 0}</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-500 opacity-50" />
            </div>
            <div className="mt-2 text-sm">
              <span className="text-green-600">{dashboard.control_effectiveness_rate || 0}% effective</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Governance Approvals</p>
                <p className="text-3xl font-bold">{dashboard.summary?.total_approvals || 0}</p>
              </div>
              <Building2 className="h-10 w-10 text-indigo-500 opacity-50" />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Management sign-offs
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Verification Rate Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Evidence Verification Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getScoreBgColor(dashboard.verification_rate)}`}
                  style={{ width: `${dashboard.verification_rate || 0}%` }}
                />
              </div>
            </div>
            <span className={`font-bold ${getScoreColor(dashboard.verification_rate).split(' ')[0]}`}>
              {dashboard.verification_rate || 0}%
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Evidence</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.recent_evidence?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_evidence.map((ev, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="font-medium text-sm">{ev.title}</p>
                        <p className="text-xs text-gray-500">{ev.evidence_type}</p>
                      </div>
                    </div>
                    <Badge variant={ev.status === 'verified' ? 'default' : 'secondary'}>
                      {ev.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent evidence</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Links</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.recent_links?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_links.map((link, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Link2 className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="font-medium text-sm">{link.target_type}</p>
                        <p className="text-xs text-gray-500">{link.target_id}</p>
                      </div>
                    </div>
                    <Badge variant="outline">{link.framework}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent links</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Evidence Registry Component
const EvidenceRegistry = ({ onRegister }) => {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [newEvidence, setNewEvidence] = useState({
    title: '',
    description: '',
    evidence_type: 'source_document',
    file_reference: '',
    source_system: '',
    tags: ''
  });

  const fetchEvidence = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/evidence-hub/evidence?limit=100`;
      if (filterType !== 'all') url += `&evidence_type=${filterType}`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setEvidence(data.evidence || []);
    } catch (error) {
      console.error('Error fetching evidence:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  const handleRegister = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(`/evidence-hub/evidence/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newEvidence,
          tags: newEvidence.tags.split(',').map(t => t.trim()).filter(t => t)
        })
      });
      
      if (response && !response._error) {
        toast.success('Evidence registered successfully');
        setShowRegisterForm(false);
        setNewEvidence({
          title: '',
          description: '',
          evidence_type: 'source_document',
          file_reference: '',
          source_system: '',
          tags: ''
        });
        fetchEvidence();
        if (onRegister) onRegister();
      } else {
        toast.error(response?.message || 'Failed to register evidence');
      }
    } catch (error) {
      toast.error('Error registering evidence');
    }
  };

  const filteredEvidence = evidence.filter(ev => 
    !searchQuery || 
    ev.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ev.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search evidence..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Evidence Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {EVIDENCE_TYPES.map(type => (
              <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowRegisterForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Register Evidence
        </Button>
      </div>

      {/* Register Form Modal */}
      {showRegisterForm && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Register New Evidence
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Title *</label>
                <Input
                  value={newEvidence.title}
                  onChange={(e) => setNewEvidence({...newEvidence, title: e.target.value})}
                  placeholder="Evidence title"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Evidence Type *</label>
                <Select 
                  value={newEvidence.evidence_type} 
                  onValueChange={(v) => setNewEvidence({...newEvidence, evidence_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVIDENCE_TYPES.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Description *</label>
              <Input
                value={newEvidence.description}
                onChange={(e) => setNewEvidence({...newEvidence, description: e.target.value})}
                placeholder="Describe the evidence"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">File Reference</label>
                <Input
                  value={newEvidence.file_reference}
                  onChange={(e) => setNewEvidence({...newEvidence, file_reference: e.target.value})}
                  placeholder="File path or document ID"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Source System</label>
                <Input
                  value={newEvidence.source_system}
                  onChange={(e) => setNewEvidence({...newEvidence, source_system: e.target.value})}
                  placeholder="e.g., SAP, Oracle, Manual"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Tags (comma-separated)</label>
              <Input
                value={newEvidence.tags}
                onChange={(e) => setNewEvidence({...newEvidence, tags: e.target.value})}
                placeholder="e.g., emissions, 2024, scope1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRegisterForm(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleRegister}
                disabled={!newEvidence.title || !newEvidence.description}
              >
                Register
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Evidence List */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
        </div>
      ) : filteredEvidence.length > 0 ? (
        <div className="space-y-3">
          {filteredEvidence.map((ev, idx) => (
            <Card key={idx} className="hover:shadow-md transition-shadow" data-testid={`evidence-item-${idx}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">{ev.title}</h4>
                      <p className="text-sm text-gray-500 mt-1">{ev.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline">{ev.evidence_type?.replace(/_/g, ' ')}</Badge>
                        {ev.source_system && (
                          <Badge variant="secondary">{ev.source_system}</Badge>
                        )}
                        {ev.tags?.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="bg-gray-100">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={ev.status === 'verified' ? 'default' : ev.status === 'rejected' ? 'destructive' : 'secondary'}
                    >
                      {ev.status === 'verified' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {ev.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                      {ev.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                      {ev.status}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No evidence found</p>
            <Button className="mt-4" variant="outline" onClick={() => setShowRegisterForm(true)}>
              Register Your First Evidence
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Compliance Scorecards Component
const ComplianceScoreCards = () => {
  const [selectedFramework, setSelectedFramework] = useState('csrd_esrs');
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchScorecard = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/evidence-hub/compliance-score/${selectedFramework}?reporting_period=2024`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setScorecard(data);
    } catch (error) {
      console.error('Error fetching scorecard:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFramework]);

  useEffect(() => {
    fetchScorecard();
  }, [fetchScorecard]);

  const fw = FRAMEWORKS.find(f => f.id === selectedFramework);

  return (
    <div className="space-y-6">
      {/* Framework Selector */}
      <div className="flex items-center gap-4">
        <label className="font-medium">Select Framework:</label>
        <Select value={selectedFramework} onValueChange={setSelectedFramework}>
          <SelectTrigger className="w-[250px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FRAMEWORKS.map(f => (
              <SelectItem key={f.id} value={f.id}>
                {f.icon} {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchScorecard}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="text-gray-500 mt-2">Calculating compliance score...</p>
        </div>
      ) : scorecard ? (
        <div className="space-y-6">
          {/* Overall Score */}
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    {fw?.icon} {fw?.name} Compliance Score
                  </h3>
                  <p className="text-gray-500 mt-1">Reporting Period: {scorecard.reporting_period}</p>
                </div>
                <div className={`text-5xl font-bold ${getScoreColor(scorecard.overall_score).split(' ')[0]}`}>
                  {scorecard.overall_score}%
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Data Completeness</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scorecard.data_completeness).split(' ')[0]}`}>
                    {scorecard.data_completeness}%
                  </p>
                  <p className="text-xs text-gray-400">
                    {scorecard.data_points_complete}/{scorecard.data_points_total} points
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Evidence Coverage</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scorecard.evidence_coverage).split(' ')[0]}`}>
                    {scorecard.evidence_coverage}%
                  </p>
                  <p className="text-xs text-gray-400">
                    {scorecard.evidence_provided}/{scorecard.evidence_required} items
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Control Effectiveness</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scorecard.control_effectiveness).split(' ')[0]}`}>
                    {scorecard.control_effectiveness}%
                  </p>
                  <p className="text-xs text-gray-400">
                    {scorecard.controls_effective}/{scorecard.controls_total} controls
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Governance Approval</p>
                  <p className={`text-2xl font-bold ${getScoreColor(scorecard.governance_approval).split(' ')[0]}`}>
                    {scorecard.governance_approval}%
                  </p>
                  <p className="text-xs text-gray-400">
                    {scorecard.approvals_obtained}/{scorecard.approvals_required} approvals
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assurance Readiness */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Assurance Readiness
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {scorecard.assurance_ready ? (
                  <Badge className="bg-green-100 text-green-700 text-lg px-4 py-2">
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                    Ready for Assurance
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-lg px-4 py-2">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Not Ready
                  </Badge>
                )}
                <span className="text-gray-500">
                  Recommended Level: <strong className="text-gray-700">
                    {scorecard.recommended_assurance_level === 'reasonable' ? 'Reasonable Assurance' :
                     scorecard.recommended_assurance_level === 'limited' ? 'Limited Assurance' : 'Not Ready'}
                  </strong>
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Critical Gaps */}
          {scorecard.critical_gaps?.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Critical Gaps ({scorecard.critical_gaps.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {scorecard.critical_gaps.map((gap, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-lg border border-red-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium capitalize">{gap.area?.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-gray-500">{gap.impact}</p>
                        </div>
                        <Badge variant="destructive">{gap.score}%</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {scorecard.recommendations?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {scorecard.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <ChevronRight className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      ) : null}
    </div>
  );
};

// Controls & Attestations Component
const ControlsAttestations = () => {
  const [attestations, setAttestations] = useState([]);
  const [controlReqs, setControlReqs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAttestForm, setShowAttestForm] = useState(false);
  const [selectedArea, setSelectedArea] = useState('emissions_data');

  const [newAttestation, setNewAttestation] = useState({
    control_id: '',
    control_name: '',
    framework: 'csrd_esrs',
    reporting_period: '2024',
    effectiveness: 'effective',
    tester: '',
    test_procedures: '',
    findings: '',
    remediation_required: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [attestData, reqsData] = await Promise.all([
        apiFetch(`/evidence-hub/controls/attestations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        apiFetch(`/evidence-hub/controls/requirements`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (attestData && !attestData._error) setAttestations(attestData.attestations || []);
      if (reqsData && !reqsData._error) setControlReqs(reqsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttest = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(`/evidence-hub/controls/attest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newAttestation,
          test_procedures: newAttestation.test_procedures.split('\n').filter(t => t.trim()),
          findings: newAttestation.findings.split('\n').filter(f => f.trim())
        })
      });

      if (response && !response._error) {
        toast.success('Control attestation recorded');
        setShowAttestForm(false);
        fetchData();
      } else {
        toast.error(response?.message || 'Failed to record attestation');
      }
    } catch (error) {
      toast.error('Error recording attestation');
    }
  };

  const selectedReqs = controlReqs?.requirements?.[selectedArea];

  return (
    <div className="space-y-6">
      {/* Control Requirements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Control Requirements by Area
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4 flex-wrap">
            {controlReqs?.areas?.map(area => (
              <Button
                key={area}
                variant={selectedArea === area ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedArea(area)}
              >
                {area.replace(/_/g, ' ')}
              </Button>
            ))}
          </div>

          {selectedReqs && (
            <div className="space-y-3">
              <h4 className="font-medium">{selectedReqs.name}</h4>
              {selectedReqs.controls?.map((ctrl, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{ctrl.control_id}: {ctrl.name}</p>
                      <p className="text-sm text-gray-500 mt-1">{ctrl.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline">{ctrl.category?.replace(/_/g, ' ')}</Badge>
                        <Badge variant="secondary">{ctrl.frequency}</Badge>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setNewAttestation({
                          ...newAttestation,
                          control_id: ctrl.control_id,
                          control_name: ctrl.name,
                          test_procedures: ctrl.test_procedures?.join('\n') || ''
                        });
                        setShowAttestForm(true);
                      }}
                    >
                      Attest
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attestation Form */}
      {showAttestForm && (
        <Card className="border-2 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-lg">Record Control Attestation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Control ID</label>
                <Input value={newAttestation.control_id} disabled />
              </div>
              <div>
                <label className="text-sm font-medium">Control Name</label>
                <Input value={newAttestation.control_name} disabled />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Framework</label>
                <Select 
                  value={newAttestation.framework}
                  onValueChange={(v) => setNewAttestation({...newAttestation, framework: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FRAMEWORKS.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Reporting Period</label>
                <Input 
                  value={newAttestation.reporting_period}
                  onChange={(e) => setNewAttestation({...newAttestation, reporting_period: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Effectiveness</label>
                <Select 
                  value={newAttestation.effectiveness}
                  onValueChange={(v) => setNewAttestation({...newAttestation, effectiveness: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="effective">Effective</SelectItem>
                    <SelectItem value="partially_effective">Partially Effective</SelectItem>
                    <SelectItem value="ineffective">Ineffective</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Tester Name</label>
              <Input 
                value={newAttestation.tester}
                onChange={(e) => setNewAttestation({...newAttestation, tester: e.target.value})}
                placeholder="Who performed the testing"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Test Procedures (one per line)</label>
              <textarea 
                className="w-full p-3 border rounded-lg min-h-[100px]"
                value={newAttestation.test_procedures}
                onChange={(e) => setNewAttestation({...newAttestation, test_procedures: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Findings (one per line)</label>
              <textarea 
                className="w-full p-3 border rounded-lg min-h-[80px]"
                value={newAttestation.findings}
                onChange={(e) => setNewAttestation({...newAttestation, findings: e.target.value})}
                placeholder="Any findings or observations"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAttestForm(false)}>Cancel</Button>
              <Button onClick={handleAttest} disabled={!newAttestation.tester}>
                Record Attestation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Attestations */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Attestations</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
            </div>
          ) : attestations.length > 0 ? (
            <div className="space-y-3">
              {attestations.map((att, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{att.control_id}: {att.control_name}</p>
                      <p className="text-sm text-gray-500">
                        Tested by {att.tester} on {new Date(att.test_date).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant={
                      att.effectiveness === 'effective' ? 'default' :
                      att.effectiveness === 'ineffective' ? 'destructive' : 'secondary'
                    }>
                      {att.effectiveness}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No attestations recorded yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Governance Approvals Component  
const GovernanceApprovals = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [newApproval, setNewApproval] = useState({
    approval_type: 'board_resolution',
    approval_subject: '',
    approval_level: 'board',
    approver_name: '',
    approver_title: '',
    reporting_period: '2024',
    framework: 'csrd_esrs',
    conditions: ''
  });

  useEffect(() => {
    fetchApprovals();
  }, []);

  const fetchApprovals = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/evidence-hub/governance/approvals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setApprovals(data.approvals || []);
    } catch (error) {
      console.error('Error fetching approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(`/evidence-hub/governance/approval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newApproval,
          conditions: newApproval.conditions.split('\n').filter(c => c.trim())
        })
      });

      if (response && !response._error) {
        toast.success('Governance approval recorded');
        setShowForm(false);
        fetchApprovals();
      } else {
        toast.error(response?.message || 'Failed to record approval');
      }
    } catch (error) {
      toast.error('Error recording approval');
    }
  };

  const approvalLevels = [
    { id: 'data_owner', name: 'Data Owner' },
    { id: 'process_owner', name: 'Process Owner' },
    { id: 'department_head', name: 'Department Head' },
    { id: 'cfo', name: 'CFO' },
    { id: 'ceo', name: 'CEO' },
    { id: 'audit_committee', name: 'Audit Committee' },
    { id: 'board', name: 'Board' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Governance Approvals
        </h3>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Record Approval
        </Button>
      </div>

      {/* Approval Form */}
      {showForm && (
        <Card className="border-2 border-indigo-200 bg-indigo-50">
          <CardHeader>
            <CardTitle className="text-lg">Record Governance Approval</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Approval Type</label>
                <Select 
                  value={newApproval.approval_type}
                  onValueChange={(v) => setNewApproval({...newApproval, approval_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="board_resolution">Board Resolution</SelectItem>
                    <SelectItem value="management_sign_off">Management Sign-Off</SelectItem>
                    <SelectItem value="committee_approval">Committee Approval</SelectItem>
                    <SelectItem value="ceo_attestation">CEO Attestation</SelectItem>
                    <SelectItem value="cfo_attestation">CFO Attestation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Approval Level</label>
                <Select 
                  value={newApproval.approval_level}
                  onValueChange={(v) => setNewApproval({...newApproval, approval_level: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {approvalLevels.map(level => (
                      <SelectItem key={level.id} value={level.id}>{level.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Approval Subject *</label>
              <Input 
                value={newApproval.approval_subject}
                onChange={(e) => setNewApproval({...newApproval, approval_subject: e.target.value})}
                placeholder="e.g., 2024 Sustainability Report"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Approver Name *</label>
                <Input 
                  value={newApproval.approver_name}
                  onChange={(e) => setNewApproval({...newApproval, approver_name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Approver Title *</label>
                <Input 
                  value={newApproval.approver_title}
                  onChange={(e) => setNewApproval({...newApproval, approver_title: e.target.value})}
                  placeholder="e.g., Chief Financial Officer"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Reporting Period</label>
                <Input 
                  value={newApproval.reporting_period}
                  onChange={(e) => setNewApproval({...newApproval, reporting_period: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Framework</label>
                <Select 
                  value={newApproval.framework}
                  onValueChange={(v) => setNewApproval({...newApproval, framework: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FRAMEWORKS.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Conditions (one per line)</label>
              <textarea 
                className="w-full p-3 border rounded-lg min-h-[80px]"
                value={newApproval.conditions}
                onChange={(e) => setNewApproval({...newApproval, conditions: e.target.value})}
                placeholder="Any conditions or caveats"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button 
                onClick={handleSubmit}
                disabled={!newApproval.approval_subject || !newApproval.approver_name || !newApproval.approver_title}
              >
                Record Approval
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approvals List */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : approvals.length > 0 ? (
        <div className="space-y-3">
          {approvals.map((app, idx) => (
            <Card key={idx}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Building2 className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">{app.approval_subject}</h4>
                      <p className="text-sm text-gray-500">
                        {app.approver_name}, {app.approver_title}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline">{app.approval_level?.replace(/_/g, ' ')}</Badge>
                        <Badge variant="secondary">{app.framework}</Badge>
                        <span className="text-xs text-gray-400">
                          {new Date(app.approval_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  {app.digital_signature_hash && (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Signed
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No governance approvals recorded</p>
            <Button className="mt-4" variant="outline" onClick={() => setShowForm(true)}>
              Record First Approval
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Workpaper Generator Component
const WorkpaperGenerator = () => {
  const [workpapers, setWorkpapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState('csrd_esrs');

  useEffect(() => {
    fetchWorkpapers();
  }, []);

  const fetchWorkpapers = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/evidence-hub/workpapers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWorkpapers(data.workpapers || []);
    } catch (error) {
      console.error('Error fetching workpapers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/evidence-hub/workpaper/generate?framework=${selectedFramework}&reporting_period=2024`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Audit workpaper generated successfully');
        fetchWorkpapers();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to generate workpaper');
      }
    } catch (error) {
      toast.error('Error generating workpaper');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Generator */}
      <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Generate Audit Workpaper
          </CardTitle>
          <CardDescription>
            Create a comprehensive audit package for external auditors
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Select value={selectedFramework} onValueChange={setSelectedFramework}>
              <SelectTrigger className="w-[250px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FRAMEWORKS.map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.icon} {f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleGenerate} disabled={generating} className="gap-2">
              {generating ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {generating ? 'Generating...' : 'Generate Workpaper'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Previous Workpapers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Previous Workpapers</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
            </div>
          ) : workpapers.length > 0 ? (
            <div className="space-y-3">
              {workpapers.map((wp, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        {FRAMEWORKS.find(f => f.id === wp.framework)?.icon}
                        {FRAMEWORKS.find(f => f.id === wp.framework)?.name} Workpaper
                      </p>
                      <p className="text-sm text-gray-500">
                        Period: {wp.reporting_period} | Generated: {new Date(wp.generated_at).toLocaleString()}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span>Score: <strong className={getScoreColor(wp.compliance_scorecard?.overall_score).split(' ')[0]}>
                          {wp.compliance_scorecard?.overall_score}%
                        </strong></span>
                        <Badge variant={wp.compliance_scorecard?.assurance_ready ? 'default' : 'secondary'}>
                          {wp.compliance_scorecard?.assurance_ready ? 'Assurance Ready' : 'Not Ready'}
                        </Badge>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="gap-2">
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No workpapers generated yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Main Component
const ComplianceEvidenceHub = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/evidence-hub/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="compliance-evidence-hub">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="h-8 w-8 text-blue-600" />
              Evidence Library
            </h1>
            <p className="text-gray-500 mt-1">
              Unified evidence registry for audit-ready compliance management
            </p>
          </div>
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="dashboard" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="evidence" className="gap-2">
            <FileText className="h-4 w-4" />
            Evidence Registry
          </TabsTrigger>
          <TabsTrigger value="scorecard" className="gap-2">
            <Shield className="h-4 w-4" />
            Compliance Score
          </TabsTrigger>
          <TabsTrigger value="controls" className="gap-2">
            <Scale className="h-4 w-4" />
            Controls
          </TabsTrigger>
          <TabsTrigger value="governance" className="gap-2">
            <Building2 className="h-4 w-4" />
            Governance
          </TabsTrigger>
          <TabsTrigger value="workpapers" className="gap-2">
            <Download className="h-4 w-4" />
            Workpapers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardOverview dashboard={dashboard} onRefresh={fetchDashboard} />
        </TabsContent>

        <TabsContent value="evidence">
          <EvidenceRegistry onRegister={fetchDashboard} />
        </TabsContent>

        <TabsContent value="scorecard">
          <ComplianceScoreCards />
        </TabsContent>

        <TabsContent value="controls">
          <ControlsAttestations />
        </TabsContent>

        <TabsContent value="governance">
          <GovernanceApprovals />
        </TabsContent>

        <TabsContent value="workpapers">
          <WorkpaperGenerator />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComplianceEvidenceHub;
